/**
 * 客戶管理頁面 (CustomerManagement)
 * 
 * 職責：
 * - 純組裝層：整合 useCustomers hook 與所有子元件
 * - 管理頁面級 state（彈窗開關、選中客戶）
 * - 將 hook 方法傳遞給子元件
 * 
 * @module CustomerManagementPage
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { useCustomers } from '../../../hooks/use-customers';
import { supabase } from '../../../services/supabaseClient';
import { getCustomerIdentityStatus } from '../../../services/identityGatewayService';
import type { IdentityDocumentSummary } from '../../../modules/identity/types';
import { CustomerAddModal } from './components/customer-add-modal';
import { CustomerEditModal } from './components/customer-edit-modal';
import { CustomerTable } from './components/customer-table';
import { CustomerFilterBar } from './components/customer-filter-bar';
import CustomerPasswordModal from './components/customer-password-modal';
import type { Customer } from '../../../modules/customer/types';

/**
 * 快速篩選卡片配置
 */
const quickFilterCards = [
  {
    key: 'total',
    label: '全部客戶',
    icon: 'ri-group-line',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    activeColor: 'bg-gray-600',
    filterLevel: 'all' as const,
  },
  {
    key: 'diamond',
    label: '鑽石客戶',
    icon: 'ri-vip-diamond-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    activeColor: 'bg-cyan-600',
    filterLevel: 'diamond' as const,
  },
  {
    key: 'gold',
    label: '儲值客戶',
    icon: 'ri-vip-crown-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    activeColor: 'bg-amber-600',
    filterLevel: 'gold' as const,
  },
  {
    key: 'silver',
    label: '同行客戶',
    icon: 'ri-building-2-line',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    activeColor: 'bg-indigo-600',
    filterLevel: 'silver' as const,
  },
  {
    key: 'problem',
    label: '問題客戶',
    icon: 'ri-error-warning-line',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    activeColor: 'bg-red-600',
    filterLevel: 'problem' as const,
  },
];

/**
 * 客戶管理頁面元件
 */
export default function CustomerManagement(): JSX.Element {
  const navigate = useNavigate();

  // ==================== Hook：客戶資料與操作 ====================

  const {
    filteredCustomers,
    loading,
    refreshCustomers,
    filter,
    setFilter,
    stats
  } = useCustomers();

  // ==================== 頁面級 State ====================

  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCreatingAuth, setIsCreatingAuth] = useState<boolean>(false);
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const [identityStatuses, setIdentityStatuses] = useState<Map<string, IdentityDocumentSummary>>(new Map());
  const [identityLoadingIds, setIdentityLoadingIds] = useState<Set<string>>(new Set());

  // Load admin role from localStorage for identity permission gating
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useState(() => { setAdminRole(localStorage.getItem('adminRole')); });

  // Phase 2: batch load identity statuses for visible customers
  useEffect(() => {
    const loadIdentityStatuses = async () => {
      const idsToLoad = filteredCustomers
        .map(c => c.id)
        .filter(id => !identityStatuses.has(id) && !identityLoadingIds.has(id));
      
      if (idsToLoad.length === 0) return;

      setIdentityLoadingIds(prev => {
        const next = new Set(prev);
        idsToLoad.forEach(id => next.add(id));
        return next;
      });

      const results = await Promise.allSettled(
        idsToLoad.map(async (id) => {
          try {
            const status = await getCustomerIdentityStatus(id);
            return { id, status };
          } catch {
            return { id, status: null };
          }
        })
      );

      setIdentityStatuses(prev => {
        const next = new Map(prev);
        results.forEach((result, idx) => {
          const id = idsToLoad[idx];
          if (result.status === 'fulfilled' && result.value.status) {
            next.set(id, result.value.status);
          }
        });
        return next;
      });

      setIdentityLoadingIds(prev => {
        const next = new Set(prev);
        idsToLoad.forEach(id => next.delete(id));
        return next;
      });
    };

    if (filteredCustomers.length > 0) {
      loadIdentityStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCustomers]);

  // ==================== 初始載入 ====================

  // ✅ 初始載入由 useCustomers hook 內部的 useEffect 負責（避免雙重查詢）
  // page 層不再重複呼叫 refreshCustomers，只在操作完成後手動刷新

  // ==================== 新增客戶處理 ====================

  const handleAddSubmit = async (data: any, avatarFile: File | null): Promise<void> => {
    setIsSubmitting(true);
    try {
      // 如果有上傳頭貼，先上傳
      let avatarUrl = data.avatar;
      if (avatarFile) {
        const fileName = `${Date.now()}_${avatarFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, avatarFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);

        avatarUrl = publicUrl;
      }

      // 清洗 payload：移除 customers schema 不存在的欄位，統一使用 avatar_url
      const { avatar, avatarUrl: _avatarUrl, createAuth, password, ...restData } = data;
      const insertPayload = {
        ...restData,
        avatar_url: avatarUrl || avatar || _avatarUrl || null,
      };

      // 新增客戶
      const { error } = await supabase
        .from('customers')
        .insert([insertPayload]);

      if (error) throw error;

      alert(`✅ 客戶「${data.name}」已新增成功`);
      setIsAddOpen(false);
      await refreshCustomers();
    } catch (err: any) {
      alert(`❌ 新增失敗：${err.message || '未知錯誤'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== 編輯客戶處理 ====================

  const handleEditSave = async (id: string, data: Partial<Customer>, avatarFile: File | null): Promise<void> => {
    setIsSubmitting(true);
    try {
      // 如果有上傳頭貼，先上傳
      let avatarUrl = (data as any).avatar;
      if (avatarFile) {
        const fileName = `${Date.now()}_${avatarFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, avatarFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);

        avatarUrl = publicUrl;
      }

      // 清洗 payload：移除 customers schema 不存在的欄位，統一使用 avatar_url
      const { avatar, avatarUrl: _avatarUrl, ...restData } = data as any;
      const updatePayload = {
        ...restData,
        avatar_url: avatarUrl || avatar || _avatarUrl || null,
      };

      // 更新客戶
      const { error } = await supabase
        .from('customers')
        .update(updatePayload)
        .eq('id', id);

      if (error) throw error;

      alert(`✅ 客戶資料已儲存`);
      setIsEditOpen(false);
      setSelectedCustomer(null);
      await refreshCustomers();
    } catch (err: any) {
      alert(`❌ 儲存失敗：${err.message || '未知錯誤'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== 建立登入帳號處理 ====================

  const handleCreateAuth = async (customerId: string, email: string, password: string): Promise<void> => {
    setIsCreatingAuth(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-customer-auth', {
        body: { customerId, email, password }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || '建立失敗');

      alert(`✅ 登入帳號已建立\n\nEmail：${email}\n密碼：${password}\n\n請將此資訊提供給客戶`);
      
      // 更新選中的客戶資料
      if (selectedCustomer) {
        setSelectedCustomer({ ...selectedCustomer, auth_user_id: data.authId });
      }
      
      await refreshCustomers();
    } catch (err: any) {
      alert(`❌ 建立失敗：${err.message || '未知錯誤'}`);
    } finally {
      setIsCreatingAuth(false);
    }
  };

  // ==================== 密碼重設處理 ====================

  const handleResetPassword = (customer: Customer): void => {
    setSelectedCustomer(customer);
    setIsResetPasswordOpen(true);
  };

  const handleResetPasswordSubmit = async (newPassword: string, confirmPassword: string): Promise<void> => {
    if (!selectedCustomer) return;

    // Validate password
    if (!newPassword || newPassword.length < 6) {
      alert('❌ 密碼至少需要 6 個字元');
      throw new Error('密碼至少需要 6 個字元');
    }

    if (newPassword !== confirmPassword) {
      alert('❌ 兩次輸入的密碼不一致');
      throw new Error('兩次輸入的密碼不一致');
    }

    // 檢查客戶是否已建立登入帳號
    if (!selectedCustomer.auth_user_id) {
      alert(
        `❌ 無法變更密碼\n\n原因：此客戶尚未建立登入帳號\n\n客戶：${selectedCustomer.name}\nEmail：${selectedCustomer.email}\n\n請先為客戶建立登入帳號後再變更密碼`
      );
      throw new Error('此客戶尚未建立登入帳號');
    }

    try {
      console.log('📞 呼叫 Edge Function: reset-customer-password');
      console.log('客戶資料:', {
        id: selectedCustomer.id,
        name: selectedCustomer.name,
        email: selectedCustomer.email,
        auth_user_id: selectedCustomer.auth_user_id
      });

      // Get current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('❌ 無法取得 session:', sessionError);
        throw new Error('請先登入管理員帳號');
      }

      console.log('✅ Session 已取得，Token 前 20 字元:', session.access_token.substring(0, 20) + '...');

      // Invoke Edge Function with auth header
      console.log('📤 發送請求到 Edge Function...');
      const { data, error } = await supabase.functions.invoke(
        'reset-customer-password',
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: {
            customerId: selectedCustomer.id,
            newPassword,
          },
        }
      );

      console.log('📥 Edge Function 完整回應:', JSON.stringify({ data, error }, null, 2));

      // 檢查是否有 error 物件
      if (error) {
        console.error('❌ Edge Function 錯誤物件:', error);
        console.error('錯誤類型:', typeof error);
        console.error('錯誤內容:', JSON.stringify(error, null, 2));
        
        // 嘗試從不同的地方取得錯誤訊息
        let errorMessage = '呼叫 Edge Function 失敗';
        
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        } else if (error.details) {
          errorMessage = error.details;
        }
        
        console.error('❌ 最終錯誤訊息:', errorMessage);
        throw new Error(errorMessage);
      }

      // 檢查 data 的結構
      console.log('📊 Data 結構:', {
        hasData: !!data,
        dataType: typeof data,
        dataKeys: data ? Object.keys(data) : [],
        success: data?.success,
        error: data?.error,
        details: data?.details
      });

      if (!data?.success) {
        console.error('❌ Edge Function 返回失敗:', data);
        const errorMessage = data?.error || data?.details || '密碼變更失敗';
        throw new Error(errorMessage);
      }

      console.log('✅ 密碼變更成功！');
      alert(
        `✅ 密碼已變更成功\n\n客戶：${selectedCustomer.name}\n新密碼：${newPassword}\n\n請將新密碼提供給客戶`
      );

      // Reset UI state
      setIsResetPasswordOpen(false);
      setSelectedCustomer(null);
    } catch (err: any) {
      console.error('❌ 變更密碼錯誤:', err);
      console.error('錯誤堆疊:', err.stack);
      
      // 提供更友善的錯誤訊息
      let userMessage = err.message || '未知錯誤';
      
      // 根據錯誤訊息提供對應的解決方案
      if (userMessage.includes('未授權') || userMessage.includes('身份驗證')) {
        userMessage += '\n\n💡 請重新登入管理員帳號';
      } else if (userMessage.includes('權限')) {
        userMessage += '\n\n💡 請確認您有管理員權限';
      } else if (userMessage.includes('登入帳號')) {
        userMessage += '\n\n💡 請先為客戶建立登入帳號';
      }
      
      alert(
        `❌ 變更失敗：${userMessage}\n\n請檢查：\n1. 客戶是否已建立登入帳號\n2. 您是否有管理員權限\n3. 網路連線是否正常`
      );
      throw err;
    }
  };

  // ==================== 其他操作處理 ====================

  const handleEdit = async (customer: Customer): Promise<void> => {
    // 補抓列表查詢未選取的重欄位（notes, address, line_id）
    try {
      const { data } = await supabase
        .from('customers')
        .select('notes, address, line_id')
        .eq('id', customer.id)
        .maybeSingle();

      setSelectedCustomer({ ...customer, ...(data || {}) });
    } catch {
      // 若補抓失敗仍允許開啟，只是這三個欄位為空
      setSelectedCustomer(customer);
    }
    setIsEditOpen(true);
  };

  const handleViewOrders = (customer: Customer): void => {
    navigate(`/admin/orders?customerId=${customer.id}`);
  };

  // ==================== 匯出客戶名單 ====================

  const handleExportCustomers = (): void => {
    try {
      // 準備匯出資料
      const exportData = filteredCustomers.map((customer) => ({
        '客戶編號': customer.id || '',
        '姓名': customer.name || '',
        'Email': customer.email || '',
        '電話': customer.phone || '',
        '會員等級': customer.member_level === 'diamond' ? '鑽石客戶' :
                   customer.member_level === 'gold' ? '儲值客戶' :
                   customer.member_level === 'silver' ? '同行客戶' :
                   customer.member_level === 'problem' ? '問題客戶' : '一般客戶',
        '狀態': customer.status === 'active' ? '啟用' :
               customer.status === 'inactive' ? '停用' : '封鎖',
        '公司名稱': customer.company || '',
        '統一編號': customer.tax_id || '',
        '地址': customer.address || '',
        '備註': customer.notes || '',
        '建立日期': customer.created_at ? new Date(customer.created_at).toLocaleDateString('zh-TW') : '',
      }));

      // 轉換為 CSV 格式
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row] || '';
            // 處理包含逗號或換行的內容
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // 加上 BOM 以支援中文
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `客戶名單_${new Date().toLocaleDateString('zh-TW').replace(/\//g, '')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`✅ 已匯出 ${exportData.length} 筆客戶資料`);
    } catch (err: any) {
      console.error('匯出失敗:', err);
      alert(`❌ 匯出失敗：${err.message || '未知錯誤'}`);
    }
  };

  // ==================== 渲染 ====================

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">客戶管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理客戶資料與會員類別</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsAddOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i> 新增客戶
            </button>
            <button
              onClick={handleExportCustomers}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-download-line"></i> 匯出客戶名單
            </button>
          </div>
        </div>

        {/* 快速篩選小卡片 */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          {quickFilterCards.map((card) => {
            const isActive = filter.filterLevel === card.filterLevel;
            const count = loading ? '—' : (stats[card.key as keyof typeof stats] || 0);

            return (
              <button
                key={card.key}
                onClick={() => setFilter(prev => ({ ...prev, filterLevel: card.filterLevel }))}
                disabled={loading}
                className={`
                  relative p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left
                  ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                  ${isActive
                    ? `${card.borderColor} ${card.bgColor} shadow-md`
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? card.activeColor : card.bgColor}`}>
                    <i className={`${card.icon} text-xl ${isActive ? 'text-white' : card.color}`}></i>
                  </div>
                  <span className={`text-2xl font-bold ${isActive ? card.color : 'text-gray-900'}`}>
                    {count}
                  </span>
                </div>
                <p className={`mt-2 text-sm font-medium ${isActive ? card.color : 'text-gray-600'}`}>
                  {card.label}
                </p>
                {isActive && !loading && (
                  <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${card.activeColor}`}></div>
                )}
              </button>
            );
          })}
        </div>

        {/* 篩選工具列 */}
        <CustomerFilterBar
          searchTerm={filter.searchTerm}
          filterLevel={filter.filterLevel}
          filterStatus={filter.filterStatus}
          onSearchChange={(value) => setFilter(prev => ({ ...prev, searchTerm: value }))}
          onLevelChange={(value) => setFilter(prev => ({ ...prev, filterLevel: value }))}
          onStatusChange={(value) => setFilter(prev => ({ ...prev, filterStatus: value }))}
        />

        {/* 客戶列表 / 載入中 skeleton */}
        {loading ? (
          <CustomerTableSkeleton />
        ) : (
          <CustomerTable
            customers={filteredCustomers}
            identityStatuses={identityStatuses}
            onResetPassword={handleResetPassword}
            onEdit={handleEdit}
            onViewOrders={handleViewOrders}
          />
        )}

        {/* 新增客戶彈窗 */}
        <CustomerAddModal
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          onSubmit={handleAddSubmit}
          isSubmitting={isSubmitting}
        />

        {/* 編輯客戶彈窗 */}
        <CustomerEditModal
          isOpen={isEditOpen}
          customer={selectedCustomer}
          identitySummary={selectedCustomer ? identityStatuses.get(selectedCustomer.id) ?? null : null}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedCustomer(null);
          }}
          onSave={handleEditSave}
          onCreateAuth={handleCreateAuth}
          isSaving={isSubmitting}
          isCreatingAuth={isCreatingAuth}
          adminRole={adminRole}
        />

        {/* 變更密碼彈窗 */}
        <CustomerPasswordModal
          customer={selectedCustomer}
          open={isResetPasswordOpen}
          onClose={() => {
            setIsResetPasswordOpen(false);
            setSelectedCustomer(null);
          }}
          onConfirm={handleResetPasswordSubmit}
        />
      </div>
    </AdminLayout>
  );
}

// ==================== 載入中 Skeleton ====================

function CustomerTableSkeleton(): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
      {/* 表頭 */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-gray-200 px-6 py-4 grid grid-cols-8 gap-4">
        {['客戶資訊', '聯絡方式', '會員類別', '折扣', '租借統計', '標籤', '狀態', '操作'].map((col) => (
          <div key={col} className="h-4 bg-gray-200 rounded w-3/4"></div>
        ))}
      </div>
      {/* 骨架列 */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="px-6 py-4 border-b border-gray-100 grid grid-cols-8 gap-4 items-center"
        >
          {/* 客戶資訊：頭像 + 名字 */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0"></div>
            <div className="space-y-1.5 flex-1">
              <div className="h-3 bg-gray-200 rounded w-4/5"></div>
              <div className="h-2.5 bg-gray-100 rounded w-3/5"></div>
            </div>
          </div>
          {/* 聯絡方式 */}
          <div className="space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            <div className="h-3 bg-gray-100 rounded w-3/5"></div>
          </div>
          {/* 會員類別 */}
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          {/* 折扣 */}
          <div className="h-3 bg-gray-200 rounded w-10"></div>
          {/* 租借統計 */}
          <div className="space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-16"></div>
            <div className="h-2.5 bg-gray-100 rounded w-20"></div>
          </div>
          {/* 標籤 */}
          <div className="flex gap-1">
            <div className="h-5 bg-gray-200 rounded-full w-12"></div>
            <div className="h-5 bg-gray-100 rounded-full w-10"></div>
          </div>
          {/* 狀態 */}
          <div className="h-6 bg-gray-200 rounded-full w-14"></div>
          {/* 操作 */}
          <div className="flex justify-center gap-2">
            <div className="w-7 h-7 bg-gray-200 rounded-lg"></div>
            <div className="w-7 h-7 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      ))}
    </div>
  );
}
